import store from '@/store'
import router from '@/router'

/**
 * 安全導航到指定路徑，防止 NavigationDuplicated 錯誤
 * @param {string} path - 目標路徑
 * @param {Object} options - 選項
 * @param {boolean} options.useReplace - 使用 replace 而非 push（預設 false）
 * @returns {Promise<void>}
 */
export async function navigateTo(path, options = {}) {
	const { useReplace = false } = options

	// 檢查是否已在目標頁面
	if (router.currentRoute.path === path) {
		return
	}

	try {
		if (useReplace) {
			await router.replace(path)
		} else {
			await router.push(path)
		}
	} catch (err) {
		if (err.name !== 'NavigationDuplicated') {
			throw err
		}
	}
}

/**
 * 導航到 Refresh 頁面
 * @param {string} random - QR Code Hash
 * @param {Object} options - 選項
 * @param {boolean} options.useReplace - 使用 replace 而非 push（預設 true）
 * @returns {Promise<void>}
 */
export async function navigateToRefresh(random, options = {}) {
	const { useReplace = true } = options
	const targetPath = `/${random}/Refresh`

	// Refresh 頁靠 isKioskDomain 或 ?kiosk= 判定前台情境（見 Refresh.vue 的 getCheck），
	// 非 Kiosk 域名時只剩 query 這個依據，掉了就會被當成訪客彈回 Store，故一併帶過去
	const kioskHash = router.currentRoute.query.kiosk
	const targetLocation = kioskHash
		? `${targetPath}?kiosk=${kioskHash}`
		: targetPath

	// 1. 取得 platformInfo.kioskUrl，優先處理跨域跳轉
	const kioskUrl = store.state.store.platformInfo?.kioskUrl

	// 2. 如果有 kioskUrl 且與當前域名不同，使用 window.location 跳轉。
	//    本地開發的 origin 必然與 kioskUrl 不同，跳轉會讓人一開 Kiosk 網址就被踢出 localhost，
	//    故僅在正式環境跳轉；改走下方的 router 導航到 Refresh，前台帳密登入在本機一樣走得通
	//    （認證是 X-Auth-Token header + localStorage，不綁域名）
	if (kioskUrl && window.location.origin !== kioskUrl) {
		if (process.env.NODE_ENV === 'production') {
			window.location.href = `${kioskUrl}${targetLocation}`
			return
		}
		$devLog(
			`[navigation] 本地開發，略過跨域跳轉至 ${kioskUrl}${targetLocation}`,
		)
	}

	// 3. 同域名：檢查是否已在 Refresh 頁面（避免 NavigationDuplicated）
	//    targetLocation 可能帶 query，比對只看路徑
	if (router.currentRoute.path === targetPath) {
		return
	}

	// 4. 使用 Vue Router 導航
	try {
		if (useReplace) {
			await router.replace(targetLocation)
		} else {
			await router.push(targetLocation)
		}
	} catch (err) {
		if (err.name !== 'NavigationDuplicated') {
			throw err
		}
	}
}
